/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, MenuProps } from "antd";
import React from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { DropdownItemLink } from "../common/DropdownItemLink";
import { routes } from "../../appRoutingHelpers";
import { DropdownItemButton } from "../common/DropdownItemButton";
import { LoadingModal } from "../../components/LoadingModal";
import { DropdownMenuWrapper } from "../common/DropdownMenuWrapper";
import { ellipsisStyle } from "../menus/components/MenuDropdown";
import { ActionSpinner } from "../../components/ActionSpinner";
import { useDuplicateProduct } from "./service/products-api";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { ProductListItem } from "./models/product";
import { getErrorMessage } from "../../error-handler";

type Props = {
  product: ProductListItem;
};

export const ProductDropdown = ({ product }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const { duplicateProduct, isLoading } = useDuplicateProduct();

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <DropdownItemLink
          to={routes.productEdit(organizationKeyName, product.id)}
          icon={<FontAwesomeIcon icon={solid("pen")} />}
        >
          Edit
        </DropdownItemLink>
      ),
    },
    {
      key: "2",
      label: (
        <DropdownItemButton
          onClick={async () => {
            try {
              const id = await duplicateProduct(product.id);
              toastr.success("Success", "Product duplicated successfully");
              navigate(routes.productEdit(organizationKeyName, id));
            } catch (error: unknown) {
              const message = getErrorMessage(error);
              toastr.error("Error", message);
            }
          }}
          icon={<FontAwesomeIcon icon={solid("clone")} />}
        >
          {isLoading ? <ActionSpinner /> : "Duplicate product"}
        </DropdownItemButton>
      ),
    },
  ];

  const dropdownInnerWrapper = css`
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    padding-left: 8px;
    padding-right: 8px;
    padding-bottom: 8px;
  `;

  return (
    <>
      <LoadingModal header="Duplicating product..." isOpen={isLoading} />
      <Dropdown
        menu={{
          items: items,
        }}
        placement="bottomRight"
        dropdownRender={(menu) => (
          <DropdownMenuWrapper>
            <div css={dropdownInnerWrapper}>
              {React.cloneElement(menu as React.ReactElement, {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  boxShadow: "none",
                  padding: "0px",
                  backgroundColor: "transparent",
                },
              })}
            </div>
          </DropdownMenuWrapper>
        )}
      >
        <div css={ellipsisStyle}>
          <FontAwesomeIcon icon={solid("ellipsis")} />
        </div>
      </Dropdown>
    </>
  );
};
